import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { listSearches, getUserInfo, listUsers, listPricelists, listParts } from "../graphql/queries";
import { Chart } from "primereact/chart";
import { AppContext } from "../App";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Alert,
  Button,
  Card,
  Divider,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Typography,
  Stack,
  Grid,
  Paper,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  TextField,
  MenuItem,
  Chip,
  Skeleton,
  Autocomplete,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import DateRangeIcon from '@mui/icons-material/DateRange';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';

// Import custom GraphQL queries for metal prices
import { getCurrentPrices, getHistoricalPrices } from "../graphql/metalQueries";

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
});

L.Marker.prototype.options.icon = DefaultIcon;

const geoUrl =
  "https://raw.githubusercontent.com/deldersveld/topojson/master/continents/north-america.json";

const Dashboard = () => {
  const [searches, setSearches] = useState([]);
  const [allSearches, setAllSearches] = useState([]); // All searches for analytics
  const [tooltipContent, setTooltipContent] = useState("");
  const [searches24Hours, setSearches24Hours] = useState(0);
  const [searches7Days, setSearches7Days] = useState(0);
  const [searches30Days, setSearches30Days] = useState(0);
  const [searchesByPartNumber, setSearchesByPartNumber] = useState({});
  const [searchesByDate, setSearchesByDate] = useState({});
  const [userInfo, setUserInfo] = useState(null);
  
  // Enhanced search analytics state
  const [searchPriceData, setSearchPriceData] = useState({
    distribution: [],
    priceRanges: { under50: 0, fiftyTo100: 0, hundredTo200: 0, over200: 0 },
    averagePrice: 0,
    minPrice: 0,
    maxPrice: 0,
    priceTrends: {},
  });
  const [timePatterns, setTimePatterns] = useState({
    hourly: {},
    dayOfWeek: {},
    peakHours: [],
  });
  const [geographicData, setGeographicData] = useState({
    byState: {},
    byCity: {},
    coordinates: [],
  });
  
  // Metal prices state
  const [currentMetalPrices, setCurrentMetalPrices] = useState(null);
  const [historicalMetalPrices, setHistoricalMetalPrices] = useState(null);
  const [metalPricesLoading, setMetalPricesLoading] = useState(false);
  const [selectedMetalTab, setSelectedMetalTab] = useState(0);
  const [timeFrame, setTimeFrame] = useState(168); // Default to 7 days (168 hours)
  
  // User stats state
  const [allUsers, setAllUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userStats, setUserStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    providers: 0,
    subscribers: 0,
    tiers: {},
    signupsByMonth: {},
    searchVolumeByUser: {},
    stateDistribution: {},
    activeUsers7d: 0,
    activeUsers30d: 0,
    powerUsers: [],
    churnUsers: [],
  });
  
  // Pricelist analytics state
  const [pricelists, setPricelists] = useState([]);
  const [pricelistLoading, setPricelistLoading] = useState(false);
  const [pricelistStats, setPricelistStats] = useState({
    mostSearched: [],
    utilization: {},
    providerPerformance: {},
  });
  
  // Part analytics state
  const [parts, setParts] = useState([]);
  const [partLoading, setPartLoading] = useState(false);
  const [partStats, setPartStats] = useState({
    mostValuable: [],
    priceDistribution: {},
    gradeBreakdown: {},
  });
  
  // Filtering and UI state
  const [selectedTab, setSelectedTab] = useState(0);
  const [dateRange, setDateRange] = useState({
    start: null,
    end: null,
    preset: '30d', // 'today', '7d', '30d', '90d', 'custom'
  });
  const [filters, setFilters] = useState({
    pricelist: null,
    partNumber: null,
    userTier: null,
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const [mapView, setMapView] = useState('markers'); // 'markers', 'heatmap', 'clusters'

  const { userRecord, userClass } = useContext(AppContext);

  // Enhanced fetchSearches with pagination support
  const fetchSearches = async () => {
    if (!userRecord || !userRecord.id) {
      console.log("No userRecord available");
      return;
    }

    // Don't fetch if dateRange hasn't been initialized yet
    if (!dateRange.start) {
      console.log("Waiting for dateRange to be initialized");
      return;
    }

    setSearchLoading(true);
    try {
      let allSearchesData = [];
      let nextToken = null;
      
      // Build filter based on date range and other filters
      const filter = {};
      
      // For Admin/SuperAdmin, don't filter by user - show all searches
      // For regular users, only show their searches
      if (userClass && !["Admin", "SuperAdmin"].includes(userClass)) {
        filter.userSearchedID = { eq: userRecord.id };
      } else if (!userClass) {
        // If userClass is not yet loaded, default to filtering by user
        filter.userSearchedID = { eq: userRecord.id };
      }
      
      // Add date range filter
      if (dateRange.start) {
        filter.createdAt = { ge: dateRange.start.toISOString() };
      }
      if (dateRange.end) {
        filter.createdAt = { 
          ...filter.createdAt, 
          le: dateRange.end.toISOString() 
        };
      }
      
      // Add other filters
      if (filters.pricelist) {
        filter.pricelistID = { eq: filters.pricelist };
      }
      if (filters.partNumber) {
        filter.partNumber = { contains: filters.partNumber };
      }
      
      console.log("Fetching searches with filter:", filter);
      
      do {
        const response = await API.graphql(
          graphqlOperation(listSearches, {
            filter: Object.keys(filter).length > 0 ? filter : undefined,
            limit: 1000,
            nextToken,
          })
        );
        
        const items = response.data.listSearches.items || [];
        allSearchesData = [...allSearchesData, ...items];
        nextToken = response.data.listSearches.nextToken;
      } while (nextToken);
      
      console.log(`Fetched ${allSearchesData.length} searches`);
      
      setSearches(allSearchesData);
      setAllSearches(allSearchesData);
      processSearchData(allSearchesData);
      processSearchPriceData(allSearchesData);
      processTimePatterns(allSearchesData);
      processGeographicData(allSearchesData);
    } catch (error) {
      console.error("Error fetching searches:", error);
      // Show error to user
      setSearches([]);
      setAllSearches([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const fetchUserInfo = async () => {
    if (userRecord && userRecord.id) {
      try {
        const response = await API.graphql(
          graphqlOperation(getUserInfo, { id: userRecord.id })
        );
        setUserInfo(response.data.getUserInfo);
      } catch (error) {
        console.log("Error fetching user info:", error);
      }
    }
  };

  // New function to fetch all users and process analytics
  const fetchUsers = async () => {
    setUserLoading(true);
    try {
      const response = await API.graphql(
        graphqlOperation(listUsers, {
          limit: 1000
        })
      );
      
      const usersData = response.data.listUsers.items;
      setAllUsers(usersData);
      processUserData(usersData);
    } catch (error) {
      console.log("Error fetching users:", error);
    } finally {
      setUserLoading(false);
    }
  };
  
  const processUserData = (usersData) => {
    // Initialize stats object
    const stats = {
      total: usersData.length,
      active: 0,
      inactive: 0,
      providers: 0,
      subscribers: 0,
      tiers: {},
      signupsByMonth: {},
      searchVolumeByUser: {},
      stateDistribution: {},
      activeUsers7d: 0,
      activeUsers30d: 0,
      powerUsers: [],
      churnUsers: [],
    };
    
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    // Map user searches for quick lookup
    const userSearchMap = {};
    allSearches.forEach(search => {
      if (search.userSearchedID) {
        if (!userSearchMap[search.userSearchedID]) {
          userSearchMap[search.userSearchedID] = [];
        }
        userSearchMap[search.userSearchedID].push(search);
      }
    });
    
    usersData.forEach(user => {
      // Count active vs inactive users (subscription status)
      if (user.subscriptionActive) {
        stats.active++;
      } else {
        stats.inactive++;
      }
      
      // Count providers vs regular users
      if (user.isProvider) {
        stats.providers++;
      } else {
        stats.subscribers++;
      }
      
      // Count users by subscription tier
      const tier = user.subscriptionTier || 'Free';
      stats.tiers[tier] = (stats.tiers[tier] || 0) + 1;
      
      // Track signups by month
      if (user.createdAt) {
        const date = new Date(user.createdAt);
        const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;
        stats.signupsByMonth[monthYear] = (stats.signupsByMonth[monthYear] || 0) + 1;
      }
      
      // Track search volume by user
      const userSearches = userSearchMap[user.id] || [];
      const searchCount = userSearches.length;
      stats.searchVolumeByUser[user.username || user.id] = searchCount;
      
      // Check active users (searched in last 7/30 days)
      const recentSearches7d = userSearches.filter(s => new Date(s.createdAt) > sevenDaysAgo);
      const recentSearches30d = userSearches.filter(s => new Date(s.createdAt) > thirtyDaysAgo);
      
      if (recentSearches7d.length > 0) stats.activeUsers7d++;
      if (recentSearches30d.length > 0) stats.activeUsers30d++;
      
      // Track geographic distribution by state
      if (user.state) {
        stats.stateDistribution[user.state] = (stats.stateDistribution[user.state] || 0) + 1;
      }
    });
    
    // Identify power users (top 10% by search volume)
    const sortedUsers = Object.entries(stats.searchVolumeByUser)
      .sort((a, b) => b[1] - a[1]);
    const top10Percent = Math.max(1, Math.floor(sortedUsers.length * 0.1));
    stats.powerUsers = sortedUsers.slice(0, top10Percent).map(([username, count]) => ({
      username,
      searchCount: count,
    }));
    
    // Identify churn users (had searches but none in last 30 days)
    usersData.forEach(user => {
      const userSearches = userSearchMap[user.id] || [];
      if (userSearches.length > 0) {
        const lastSearch = userSearches.sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        )[0];
        const daysSinceLastSearch = (now - new Date(lastSearch.createdAt)) / (1000 * 60 * 60 * 24);
        if (daysSinceLastSearch > 30) {
          stats.churnUsers.push({
            username: user.username || user.id,
            lastSearch: lastSearch.createdAt,
            daysSince: Math.floor(daysSinceLastSearch),
          });
        }
      }
    });
    
    setUserStats(stats);
  };

  // New function to fetch metal prices
  const fetchMetalPrices = async () => {
    setMetalPricesLoading(true);
    try {
      // Fetch current metal prices
      const currentPricesResponse = await API.graphql(
        graphqlOperation(getCurrentPrices)
      );
      setCurrentMetalPrices(currentPricesResponse.data.getCurrentPrices.current);

      // Fetch historical metal prices
      const historicalPricesResponse = await API.graphql(
        graphqlOperation(getHistoricalPrices, { hours: timeFrame })
      );
      setHistoricalMetalPrices(historicalPricesResponse.data.getHistoricalPrices.history);
    } catch (error) {
      console.log("Error fetching metal prices:", error);
    } finally {
      setMetalPricesLoading(false);
    }
  };

  const processSearchData = (searchesData) => {
    const last24Hours = new Date();
    last24Hours.setDate(last24Hours.getDate() - 1);
    const last7Days = new Date();
    last7Days.setDate(last7Days.getDate() - 7);
    const last30Days = new Date();
    last30Days.setDate(last30Days.getDate() - 30);

    const partNumberCounts = {};
    const dateCounts = {};
    let count24h = 0, count7d = 0, count30d = 0;

    searchesData.forEach((search) => {
      const createdAt = new Date(search.createdAt);

      // Count searches by part number
      partNumberCounts[search.partNumber] = (partNumberCounts[search.partNumber] || 0) + 1;

      // Count searches by date
      const dateString = createdAt.toISOString().split('T')[0];
      dateCounts[dateString] = (dateCounts[dateString] || 0) + 1;

      // Count searches for different time periods
      if (createdAt > last24Hours) count24h++;
      if (createdAt > last7Days) count7d++;
      if (createdAt > last30Days) count30d++;
    });

    setSearches24Hours(count24h);
    setSearches7Days(count7d);
    setSearches30Days(count30d);
    setSearchesByPartNumber(partNumberCounts);
    setSearchesByDate(dateCounts);
  };
  
  // Process price distribution and trends
  const processSearchPriceData = (searchesData) => {
    if (!searchesData || searchesData.length === 0) return;
    
    const prices = searchesData.map(s => s.price).filter(p => p != null);
    if (prices.length === 0) return;
    
    const sortedPrices = [...prices].sort((a, b) => a - b);
    const minPrice = sortedPrices[0];
    const maxPrice = sortedPrices[sortedPrices.length - 1];
    const averagePrice = prices.reduce((sum, p) => sum + p, 0) / prices.length;
    
    // Price ranges
    const priceRanges = {
      under50: prices.filter(p => p < 50).length,
      fiftyTo100: prices.filter(p => p >= 50 && p < 100).length,
      hundredTo200: prices.filter(p => p >= 100 && p < 200).length,
      over200: prices.filter(p => p >= 200).length,
    };
    
    // Price distribution for histogram (bins)
    const binSize = Math.max(10, (maxPrice - minPrice) / 20);
    const distribution = [];
    for (let i = minPrice; i <= maxPrice; i += binSize) {
      const count = prices.filter(p => p >= i && p < i + binSize).length;
      distribution.push({
        range: `$${i.toFixed(0)}-$${(i + binSize).toFixed(0)}`,
        count,
        min: i,
        max: i + binSize,
      });
    }
    
    // Price trends by date
    const priceTrends = {};
    searchesData.forEach(search => {
      const date = new Date(search.createdAt).toISOString().split('T')[0];
      if (!priceTrends[date]) {
        priceTrends[date] = { prices: [], count: 0 };
      }
      priceTrends[date].prices.push(search.price);
      priceTrends[date].count++;
    });
    
    // Calculate average price per day
    Object.keys(priceTrends).forEach(date => {
      const prices = priceTrends[date].prices;
      priceTrends[date].average = prices.reduce((sum, p) => sum + p, 0) / prices.length;
    });
    
    setSearchPriceData({
      distribution,
      priceRanges,
      averagePrice,
      minPrice,
      maxPrice,
      priceTrends,
    });
  };
  
  // Process time-based patterns
  const processTimePatterns = (searchesData) => {
    const hourly = {};
    const dayOfWeek = {};
    
    searchesData.forEach(search => {
      const date = new Date(search.createdAt);
      const hour = date.getHours();
      const day = date.getDay(); // 0 = Sunday, 6 = Saturday
      
      hourly[hour] = (hourly[hour] || 0) + 1;
      dayOfWeek[day] = (dayOfWeek[day] || 0) + 1;
    });
    
    // Find peak hours (top 3)
    const peakHours = Object.entries(hourly)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([hour]) => parseInt(hour));
    
    setTimePatterns({
      hourly,
      dayOfWeek,
      peakHours,
    });
  };
  
  // Process geographic data
  const processGeographicData = (searchesData) => {
    const byState = {};
    const byCity = {};
    const coordinates = [];
    
    searchesData.forEach(search => {
      if (search.latitude && search.longitude) {
        coordinates.push([search.latitude, search.longitude]);
      }
      
      if (search.location) {
        // Parse location string (format may vary)
        const parts = search.location.split(',').map(s => s.trim());
        if (parts.length >= 2) {
          const city = parts[0];
          const state = parts[parts.length - 1];
          
          byCity[city] = (byCity[city] || 0) + 1;
          byState[state] = (byState[state] || 0) + 1;
        }
      }
    });
    
    setGeographicData({
      byState,
      byCity,
      coordinates,
    });
  };
  
  // Fetch pricelists
  const fetchPricelists = async () => {
    setPricelistLoading(true);
    try {
      const response = await API.graphql(
        graphqlOperation(listPricelists, { limit: 1000 })
      );
      const pricelistsData = response.data.listPricelists.items || [];
      setPricelists(pricelistsData);
      processPricelistAnalytics(pricelistsData);
    } catch (error) {
      console.log("Error fetching pricelists:", error);
    } finally {
      setPricelistLoading(false);
    }
  };
  
  // Process pricelist analytics
  const processPricelistAnalytics = (pricelistsData) => {
    // This will be enhanced when we have search data with pricelist relationships
    const mostSearched = [];
    const utilization = {};
    
    // Count searches per pricelist
    const searchesByPricelist = {};
    searches.forEach(search => {
      if (search.pricelistID) {
        searchesByPricelist[search.pricelistID] = (searchesByPricelist[search.pricelistID] || 0) + 1;
      }
    });
    
    // Match with pricelist data
    pricelistsData.forEach(pl => {
      const searchCount = searchesByPricelist[pl.id] || 0;
      mostSearched.push({
        id: pl.id,
        name: pl.name || pl.filename || 'Unnamed',
        searchCount,
        validRows: pl.validRows || 0,
        utilization: pl.validRows > 0 ? (searchCount / pl.validRows) * 100 : 0,
      });
    });
    
    mostSearched.sort((a, b) => b.searchCount - a.searchCount);
    
    setPricelistStats({
      mostSearched: mostSearched.slice(0, 10),
      utilization,
      providerPerformance: {},
    });
  };
  
  // Fetch parts
  const fetchParts = async () => {
    setPartLoading(true);
    try {
      const response = await API.graphql(
        graphqlOperation(listParts, { limit: 1000 })
      );
      const partsData = response.data.listParts.items || [];
      setParts(partsData);
      processPartAnalytics(partsData);
    } catch (error) {
      console.log("Error fetching parts:", error);
    } finally {
      setPartLoading(false);
    }
  };
  
  // Process part analytics
  const processPartAnalytics = (partsData) => {
    // Most valuable parts (by average price)
    const partsByNumber = {};
    partsData.forEach(part => {
      if (!partsByNumber[part.partNumber]) {
        partsByNumber[part.partNumber] = { prices: [], count: 0, grade: part.grade };
      }
      partsByNumber[part.partNumber].prices.push(part.price);
      partsByNumber[part.partNumber].count++;
    });
    
    const mostValuable = Object.entries(partsByNumber)
      .map(([partNumber, data]) => ({
        partNumber,
        averagePrice: data.prices.reduce((sum, p) => sum + p, 0) / data.prices.length,
        minPrice: Math.min(...data.prices),
        maxPrice: Math.max(...data.prices),
        count: data.count,
        grade: data.grade,
      }))
      .sort((a, b) => b.averagePrice - a.averagePrice)
      .slice(0, 20);
    
    // Grade breakdown
    const gradeBreakdown = {};
    partsData.forEach(part => {
      const grade = part.grade || 'unknown';
      gradeBreakdown[grade] = (gradeBreakdown[grade] || 0) + 1;
    });
    
    setPartStats({
      mostValuable,
      priceDistribution: {},
      gradeBreakdown,
    });
  };

  // Handle metal tab change
  const handleMetalTabChange = (event, newValue) => {
    setSelectedMetalTab(newValue);
  };

  // Handle time frame change
  const handleTimeFrameChange = (hours) => {
    setTimeFrame(hours);
  };

  // Handle date range preset changes
  const handleDateRangePreset = (preset) => {
    const end = new Date();
    const start = new Date();
    
    switch (preset) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        break;
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
      case 'custom':
        // Custom date picker will be handled separately
        return;
      default:
        start.setDate(start.getDate() - 30);
    }
    
    setDateRange({ start, end, preset });
    // useEffect will automatically trigger fetchSearches when dateRange.preset changes
  };
  
  // Helper function for debounce
  const timeoutRef = useRef(null);
  const useDebounce = (callback, delay) => {
    return useCallback((...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    }, [callback, delay]);
  };
  
  const debouncedFetchSearches = useDebounce(() => {
    if (userRecord) {
      fetchSearches();
    }
  }, 500);
  
  // Initialize date range first, then fetch data
  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30); // Default to 30 days
    setDateRange({
      start,
      end,
      preset: '30d',
    });
  }, []);

  // Fetch all data when userRecord is available
  useEffect(() => {
    if (userRecord) {
      fetchUserInfo();
      fetchMetalPrices();
      fetchUsers();
      fetchPricelists();
      fetchParts();
    }
  }, [userRecord]);
  
  // Fetch searches when dateRange is initialized and userRecord is available
  useEffect(() => {
    if (userRecord && dateRange.start) {
      fetchSearches();
    }
  }, [userRecord, dateRange.start, dateRange.end]);
  
  // Refetch when filters or date range preset changes
  useEffect(() => {
    if (userRecord && dateRange.start) {
      debouncedFetchSearches();
    }
  }, [filters.pricelist, filters.partNumber, filters.userTier, dateRange.preset]);
  
  // Update user stats when searches are loaded
  useEffect(() => {
    if (allUsers.length > 0 && searches.length > 0) {
      processUserData(allUsers);
    }
  }, [searches]);
  
  // Update pricelist stats when searches change
  useEffect(() => {
    if (pricelists.length > 0 && searches.length > 0) {
      processPricelistAnalytics(pricelists);
    }
  }, [searches, pricelists]);

  console.log("IN ANALYTICS")
  // Refetch historical prices when time frame changes
  useEffect(() => {
    if (userRecord) {
      const fetchHistoricalPricesOnly = async () => {
        setMetalPricesLoading(true);
        try {
          const historicalPricesResponse = await API.graphql(
            graphqlOperation(getHistoricalPrices, { hours: timeFrame })
          );
          setHistoricalMetalPrices(historicalPricesResponse.data.getHistoricalPrices.history);
        } catch (error) {
          console.log("Error fetching historical metal prices:", error);
        } finally {
          setMetalPricesLoading(false);
        }
      };
      
      fetchHistoricalPricesOnly();
    }
  }, [timeFrame]);

  // Prepare user statistics charts
  const prepareUserTierChart = () => {
    const labels = Object.keys(userStats.tiers);
    const data = Object.values(userStats.tiers);
    
    // Color palette for tiers
    const backgroundColors = [
      '#FF6384',
      '#36A2EB',
      '#FFCE56',
      '#4BC0C0',
      '#9966FF',
      '#FF9F40',
    ];
    
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: backgroundColors.slice(0, labels.length),
          hoverBackgroundColor: backgroundColors.slice(0, labels.length).map(color => color + 'CC'),
        }
      ]
    };
  };
  
  const prepareSignupTrendChart = () => {
    // Sort months chronologically
    const sortedMonths = Object.keys(userStats.signupsByMonth).sort((a, b) => {
      const [monthA, yearA] = a.split('/').map(Number);
      const [monthB, yearB] = b.split('/').map(Number);
      
      if (yearA !== yearB) return yearA - yearB;
      return monthA - monthB;
    });
    
    const data = sortedMonths.map(month => userStats.signupsByMonth[month]);
    
    return {
      labels: sortedMonths,
      datasets: [
        {
          label: 'New Signups',
          data,
          fill: false,
          borderColor: '#36A2EB',
          tension: 0.1
        }
      ]
    };
  };
  
  const prepareStateDistributionChart = () => {
    // Get the top 10 states by user count
    const topStates = Object.entries(userStats.stateDistribution)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
    
    const labels = topStates.map(([state]) => state);
    const data = topStates.map(([, count]) => count);
    
    return {
      labels,
      datasets: [
        {
          label: 'Users by State',
          data,
          backgroundColor: '#4BC0C0',
          borderColor: '#36A2EB',
          borderWidth: 1
        }
      ]
    };
  };

  // Prepare data for charts
  const top10Searches = Object.entries(searchesByPartNumber)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  const lineChartData = {
    labels: Object.keys(searchesByDate).slice(-30),
    datasets: [{
      label: 'Searches',
      data: Object.values(searchesByDate).slice(-30),
      fill: false,
      borderColor: '#4bc0c0'
    }]
  };
  
  // Prepare price distribution histogram
  const preparePriceDistributionChart = () => {
    if (!searchPriceData.distribution || searchPriceData.distribution.length === 0) {
      return null;
    }
    
    const labels = searchPriceData.distribution.map(d => d.range);
    const data = searchPriceData.distribution.map(d => d.count);
    
    return {
      labels,
      datasets: [{
        label: 'Price Distribution',
        data,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }]
    };
  };
  
  // Prepare price trends chart
  const preparePriceTrendsChart = () => {
    if (!searchPriceData.priceTrends || Object.keys(searchPriceData.priceTrends).length === 0) {
      return null;
    }
    
    const sortedDates = Object.keys(searchPriceData.priceTrends).sort();
    const labels = sortedDates.slice(-30); // Last 30 days
    const averages = labels.map(date => searchPriceData.priceTrends[date].average);
    
    return {
      labels,
      datasets: [{
        label: 'Average Price',
        data: averages,
        fill: true,
        borderColor: '#FF6384',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.1
      }]
    };
  };
  
  // Prepare hourly pattern chart
  const prepareHourlyPatternChart = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const labels = hours.map(h => `${h}:00`);
    const data = hours.map(h => timePatterns.hourly[h] || 0);
    
    return {
      labels,
      datasets: [{
        label: 'Searches by Hour',
        data,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }]
    };
  };
  
  // Prepare day of week pattern chart
  const prepareDayOfWeekChart = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const data = days.map((_, index) => timePatterns.dayOfWeek[index] || 0);
    
    return {
      labels: days,
      datasets: [{
        label: 'Searches by Day',
        data,
        backgroundColor: 'rgba(153, 102, 255, 0.6)',
        borderColor: 'rgba(153, 102, 255, 1)',
        borderWidth: 1
      }]
    };
  };
  
  // Prepare pricelist performance chart
  const preparePricelistChart = () => {
    if (!pricelistStats.mostSearched || pricelistStats.mostSearched.length === 0) {
      return null;
    }
    
    const labels = pricelistStats.mostSearched.map(pl => pl.name.substring(0, 20));
    const data = pricelistStats.mostSearched.map(pl => pl.searchCount);
    
    return {
      labels,
      datasets: [{
        label: 'Search Count',
        data,
        backgroundColor: 'rgba(255, 159, 64, 0.6)',
        borderColor: 'rgba(255, 159, 64, 1)',
        borderWidth: 1
      }]
    };
  };

  const lineChartOptions = {
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Searches'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Date'
        }
      }
    }
  };

  // Prepare metal prices chart data
  const prepareMetalPricesChartData = () => {
    if (!historicalMetalPrices) return null;

    const metals = ['platinum', 'palladium', 'rhodium'];
    const selectedMetal = metals[selectedMetalTab];
    
    const labels = [];
    const data = [];
    
    historicalMetalPrices.forEach(record => {
      if (record.prices[selectedMetal]) {
        const date = new Date(record.timestamp);
        labels.push(date.toLocaleString());
        data.push(record.prices[selectedMetal].price);
      }
    });
    
    // Reverse arrays to show oldest first
    labels.reverse();
    data.reverse();
    
    const colors = {
      platinum: '#7b7e81',
      palladium: '#cbc1b8',
      rhodium: '#e2e1e4'
    };
    
    return {
      labels,
      datasets: [
        {
          label: selectedMetal.charAt(0).toUpperCase() + selectedMetal.slice(1),
          data,
          fill: false,
          borderColor: colors[selectedMetal],
          tension: 0.1
        }
      ]
    };
  };
  
  const metalPricesChartOptions = {
    scales: {
      y: {
        beginAtZero: false,
        title: {
          display: true,
          text: 'Price (USD)'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Date/Time'
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45
        }
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function(context) {
            return `$${context.raw.toFixed(2)}`;
          }
        }
      }
    }
  };
  
  const renderMetalPriceCards = () => {
    if (!currentMetalPrices || !currentMetalPrices.prices) {
      return (
        <Box display="flex" justifyContent="center" mt={4} mb={4}>
          <CircularProgress />
        </Box>
      );
    }
    
    const metals = [
      { name: 'Platinum', key: 'platinum', color: '#7b7e81' },
      { name: 'Palladium', key: 'palladium', color: '#cbc1b8' },
      { name: 'Rhodium', key: 'rhodium', color: '#e2e1e4' }
    ];
    
    return (
      <Grid container spacing={3} justifyContent="center">
        {metals.map(metal => (
          <Grid item xs={12} sm={6} md={4} key={metal.key}>
            <Card sx={{ 
              textAlign: 'center', 
              p: 3,
              height: '100%',
              background: `linear-gradient(135deg, ${metal.color}15 0%, ${metal.color}05 100%)`,
              border: `2px solid ${metal.color}40`,
              borderRadius: 2,
              boxShadow: 3,
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: 6,
                borderColor: metal.color
              }
            }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: metal.color }}>
                {metal.name}
              </Typography>
              <Typography variant="h2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                ${currentMetalPrices.prices[metal.key]?.price.toFixed(2)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Last updated: {new Date(currentMetalPrices.prices[metal.key]?.date).toLocaleString()}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Header with Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 700, color: '#1976d2' }}>
          Analytics Dashboard
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange.preset}
                label="Date Range"
                onChange={(e) => handleDateRangePreset(e.target.value)}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
                <MenuItem value="90d">Last 90 Days</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Pricelist</InputLabel>
              <Select
                value={filters.pricelist || ''}
                label="Pricelist"
                onChange={(e) => setFilters({ ...filters, pricelist: e.target.value || null })}
              >
                <MenuItem value="">All Pricelists</MenuItem>
                {pricelists.map(pl => (
                  <MenuItem key={pl.id} value={pl.id}>
                    {pl.name || pl.filename || 'Unnamed'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={Object.keys(searchesByPartNumber)}
              value={filters.partNumber || null}
              onChange={(e, newValue) => setFilters({ ...filters, partNumber: newValue })}
              renderInput={(params) => (
                <TextField {...params} label="Part Number" placeholder="Filter by part..." />
              )}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>User Tier</InputLabel>
              <Select
                value={filters.userTier || ''}
                label="User Tier"
                onChange={(e) => setFilters({ ...filters, userTier: e.target.value || null })}
              >
                <MenuItem value="">All Tiers</MenuItem>
                {Object.keys(userStats.tiers).map(tier => (
                  <MenuItem key={tier} value={tier}>{tier}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: '0.95rem',
              textTransform: 'none',
              minHeight: 64,
            },
            '& .Mui-selected': {
              color: '#4CAF50',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#4CAF50',
              height: 3,
            }
          }}
        >
          <Tab label="Overview" />
          <Tab label="Searches" />
          <Tab label="Users" />
          <Tab label="Pricelists" />
          <Tab label="Parts" />
          <Tab label="Metals" />
        </Tabs>
      </Paper>
      
      {/* Display loading state */}
      {searchLoading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}
      
      {/* Tab Content */}
      {selectedTab === 0 && (
        <Box>
          {/* Overview Tab - Key Metrics */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 2, textAlign: 'center', height: '100%', boxShadow: 3 }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                  {searches24Hours}
      </Typography>
                <Typography variant="subtitle1">Searches (24h)</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 2, textAlign: 'center', height: '100%', boxShadow: 3 }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#2196F3' }}>
                  {searches7Days}
                </Typography>
                <Typography variant="subtitle1">Searches (7d)</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 2, textAlign: 'center', height: '100%', boxShadow: 3 }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#9C27B0' }}>
                  {userStats.total}
                </Typography>
                <Typography variant="subtitle1">Total Users</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 2, textAlign: 'center', height: '100%', boxShadow: 3 }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
                  ${searchPriceData.averagePrice?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="subtitle1">Avg Price</Typography>
              </Card>
            </Grid>
          </Grid>
          
          {/* Quick Charts */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Search Trend</Typography>
                <Box sx={{ height: 300 }}>
                  <Chart type="line" data={lineChartData} options={lineChartOptions} />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Price Distribution</Typography>
                <Box sx={{ height: 300 }}>
                  {preparePriceDistributionChart() ? (
                    <Chart type="bar" data={preparePriceDistributionChart()} />
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography>No data available</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
      
      {selectedTab === 1 && (
        <Box>
          {/* Searches Tab */}
          <Typography variant="h4" sx={{ mb: 4, fontWeight: 700, color: 'text.primary' }}>
            Search Analytics
          </Typography>
          
          {/* Search Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                p: 3, 
                textAlign: 'center',
                height: '100%',
                background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                color: 'white',
                boxShadow: 3,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {searches24Hours}
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>Last 24 Hours</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                p: 3, 
                textAlign: 'center',
                height: '100%',
                background: 'linear-gradient(135deg, #2196F3 0%, #1976d2 100%)',
                color: 'white',
                boxShadow: 3,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {searches7Days}
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>Last 7 Days</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                p: 3, 
                textAlign: 'center',
                height: '100%',
                background: 'linear-gradient(135deg, #9C27B0 0%, #7b1fa2 100%)',
                color: 'white',
                boxShadow: 3,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {searches30Days}
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>Last 30 Days</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                p: 3, 
                textAlign: 'center',
                height: '100%',
                background: 'linear-gradient(135deg, #FF9800 0%, #f57c00 100%)',
                color: 'white',
                boxShadow: 3,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  ${searchPriceData.averagePrice?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>Avg Price</Typography>
              </Card>
            </Grid>
          </Grid>
          
          {/* Price Range Cards */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Price Range Distribution</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  p: 2.5, 
                  textAlign: 'center',
                  bgcolor: '#e3f2fd',
                  border: '2px solid #90caf9',
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: '#bbdefb',
                    transform: 'scale(1.02)'
                  }
                }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 0.5 }}>
                    {searchPriceData.priceRanges.under50}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>Under $50</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  p: 2.5, 
                  textAlign: 'center',
                  bgcolor: '#f3e5f5',
                  border: '2px solid #ce93d8',
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: '#e1bee7',
                    transform: 'scale(1.02)'
                  }
                }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#7b1fa2', mb: 0.5 }}>
                    {searchPriceData.priceRanges.fiftyTo100}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>$50 - $100</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  p: 2.5, 
                  textAlign: 'center',
                  bgcolor: '#fff3e0',
                  border: '2px solid #ffb74d',
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: '#ffe0b2',
                    transform: 'scale(1.02)'
                  }
                }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#e65100', mb: 0.5 }}>
                    {searchPriceData.priceRanges.hundredTo200}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>$100 - $200</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  p: 2.5, 
                  textAlign: 'center',
                  bgcolor: '#e8f5e9',
                  border: '2px solid #81c784',
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: '#c8e6c9',
                    transform: 'scale(1.02)'
                  }
                }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32', mb: 0.5 }}>
                    {searchPriceData.priceRanges.over200}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>Over $200</Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>
          
          {/* Charts Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Analytics Charts</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <Paper sx={{ 
                  p: 3, 
                  height: '100%',
                  borderRadius: 2,
                  boxShadow: 2,
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: 4
                  }
                }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Price Trends</Typography>
                  <Box sx={{ height: 350 }}>
                    {preparePriceTrendsChart() ? (
                      <Chart type="line" data={preparePriceTrendsChart()} />
                    ) : (
                      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <Typography color="text.secondary">No data available</Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Paper sx={{ 
                  p: 3, 
                  height: '100%',
                  borderRadius: 2,
                  boxShadow: 2,
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: 4
                  }
                }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Hourly Pattern</Typography>
                  <Box sx={{ height: 350 }}>
                    <Chart type="bar" data={prepareHourlyPatternChart()} />
                  </Box>
                  {timePatterns.peakHours.length > 0 && (
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: 'primary.light', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Peak hours: {timePatterns.peakHours.map(h => `${h}:00`).join(', ')}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Paper sx={{ 
                  p: 3, 
                  height: '100%',
                  borderRadius: 2,
                  boxShadow: 2,
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: 4
                  }
                }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Day of Week Pattern</Typography>
                  <Box sx={{ height: 350 }}>
                    <Chart type="bar" data={prepareDayOfWeekChart()} />
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Paper sx={{ 
                  p: 3, 
                  height: '100%',
                  borderRadius: 2,
                  boxShadow: 2,
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: 4
                  }
                }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Search Trend</Typography>
                  <Box sx={{ height: 350 }}>
                    <Chart type="line" data={lineChartData} options={lineChartOptions} />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
          
          {/* Map Section */}
          <Box sx={{ mb: 4 }}>
            <Paper sx={{ 
              p: 3, 
              borderRadius: 2,
              boxShadow: 2
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Geographic Distribution</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant={mapView === 'markers' ? 'contained' : 'outlined'}
                    onClick={() => setMapView('markers')}
                    sx={{ 
                      fontWeight: 600,
                      ...(mapView === 'markers' && {
                        bgcolor: '#4CAF50',
                        '&:hover': { bgcolor: '#45a049' }
                      })
                    }}
                  >
                    Markers
                  </Button>
                  <Button
                    size="small"
                    variant={mapView === 'clusters' ? 'contained' : 'outlined'}
                    onClick={() => setMapView('clusters')}
                    sx={{ 
                      fontWeight: 600,
                      ...(mapView === 'clusters' && {
                        bgcolor: '#4CAF50',
                        '&:hover': { bgcolor: '#45a049' }
                      })
                    }}
                  >
                    Clusters
                  </Button>
                </Box>
              </Box>
              {userRecord && userRecord.id && (
                <Box sx={{ 
                  height: 500, 
                  width: '100%',
                  borderRadius: 1,
                  overflow: 'hidden'
                }}>
                  <MapContainer
                    center={[35.4676, -97.5164]}
                    zoom={4}
                    scrollWheelZoom={false}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {searches.map((search) => (
                      search.latitude && search.longitude && (
                        <Marker
                          key={search.id}
                          position={[search.latitude, search.longitude]}
                        >
                          <Popup>
                            <Typography variant="body2"><strong>Part:</strong> {search.partNumber}</Typography>
                            <Typography variant="body2"><strong>Price:</strong> ${search.price?.toFixed(2)}</Typography>
                            <Typography variant="body2"><strong>Date:</strong> {new Date(search.createdAt).toLocaleDateString()}</Typography>
                          </Popup>
                        </Marker>
                      )
                    ))}
                  </MapContainer>
                </Box>
              )}
            </Paper>
          </Box>
          
          {/* Top Searched Parts Table */}
          <Paper sx={{ 
            p: 3,
            borderRadius: 2,
            boxShadow: 2
          }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Top Searched Parts</Typography>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Part Number</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}># of Searches</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Avg Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {top10Searches.length > 0 ? (
                  top10Searches.map(([partNumber, count], index) => {
                    const partSearches = allSearches.filter(s => s.partNumber === partNumber);
                    const avgPrice = partSearches.length > 0
                      ? (partSearches.reduce((sum, s) => sum + (s.price || 0), 0) / partSearches.length)
                      : 0;
                    return (
                      <TableRow 
                        key={partNumber}
                        sx={{ 
                          '&:hover': { bgcolor: 'action.hover' },
                          '&:nth-of-type(even)': { bgcolor: 'rgba(0,0,0,0.02)' }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{partNumber}</TableCell>
                        <TableCell>{count}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                          ${avgPrice.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No search data available</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}
      
      {selectedTab === 2 && (
        <Box>
          {/* Users Tab */}
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>User Analytics</Typography>
          
          {/* User Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                  {userStats.total}
                </Typography>
                <Typography variant="subtitle2">Total Users</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196F3' }}>
                  {userStats.activeUsers7d}
                </Typography>
                <Typography variant="subtitle2">Active (7d)</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#9C27B0' }}>
                  {userStats.providers}
                </Typography>
                <Typography variant="subtitle2">Providers</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
                  {userStats.churnUsers.length}
                </Typography>
                <Typography variant="subtitle2">Churn Risk</Typography>
              </Card>
            </Grid>
          </Grid>
          
          {/* Charts */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Subscription Tiers</Typography>
                  <Tooltip title="Distribution of users across subscription tiers">
                    <IconButton size="small">
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ height: 300 }}>
                  <Chart type="doughnut" data={prepareUserTierChart()} options={{ 
                    plugins: {
                      legend: {
                        position: 'right'
                      }
                    }
                  }} />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>User Growth Trend</Typography>
                <Box sx={{ height: 300 }}>
                  <Chart type="line" data={prepareSignupTrendChart()} options={{
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'New Users'
                        }
                      },
                      x: {
                        title: {
                          display: true,
                          text: 'Month/Year'
                        }
                      }
                    }
                  }} />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Geographic Distribution</Typography>
                <Box sx={{ height: 300 }}>
                  <Chart type="bar" data={prepareStateDistributionChart()} options={{
                    indexAxis: 'y',
                    plugins: {
                      legend: {
                        display: false
                      }
                    },
                    scales: {
                      x: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Number of Users'
                        }
                      }
                    }
                  }} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Power Users Table */}
          {userStats.powerUsers.length > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Power Users (Top 10%)</Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Username</strong></TableCell>
                    <TableCell><strong>Search Count</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userStats.powerUsers.slice(0, 10).map((user, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.searchCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      )}
      
      {selectedTab === 3 && (
        <Box>
          {/* Pricelists Tab */}
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>Pricelist Analytics</Typography>
          
          {pricelistLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Most Searched Pricelists</Typography>
                <Box sx={{ height: 400 }}>
                  {preparePricelistChart() ? (
                    <Chart type="bar" data={preparePricelistChart()} />
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography>No pricelist data available</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
              
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Pricelist Performance</Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Pricelist Name</strong></TableCell>
                      <TableCell><strong>Search Count</strong></TableCell>
                      <TableCell><strong>Valid Rows</strong></TableCell>
                      <TableCell><strong>Utilization %</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pricelistStats.mostSearched.map((pl) => (
                      <TableRow key={pl.id}>
                        <TableCell>{pl.name}</TableCell>
                        <TableCell>{pl.searchCount}</TableCell>
                        <TableCell>{pl.validRows}</TableCell>
                        <TableCell>{pl.utilization.toFixed(1)}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </>
          )}
        </Box>
      )}
      
      {selectedTab === 4 && (
        <Box>
          {/* Parts Tab */}
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>Part Analytics</Typography>
          
          {partLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Most Valuable Parts</Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Part Number</strong></TableCell>
                      <TableCell><strong>Avg Price</strong></TableCell>
                      <TableCell><strong>Min Price</strong></TableCell>
                      <TableCell><strong>Max Price</strong></TableCell>
                      <TableCell><strong>Count</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {partStats.mostValuable.slice(0, 20).map((part, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{part.partNumber}</TableCell>
                        <TableCell>${part.averagePrice.toFixed(2)}</TableCell>
                        <TableCell>${part.minPrice.toFixed(2)}</TableCell>
                        <TableCell>${part.maxPrice.toFixed(2)}</TableCell>
                        <TableCell>{part.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </>
          )}
        </Box>
      )}
      
      {selectedTab === 5 && (
        <Box>
          {/* Metals Tab */}
          <Typography variant="h4" sx={{ mb: 4, fontWeight: 700, color: 'text.primary' }}>
            Precious Metal Prices Analytics
          </Typography>
          
          {/* Metal Price Cards */}
          <Box sx={{ mb: 5 }}>
            {renderMetalPriceCards()}
          </Box>
          
          {/* Chart Section */}
          <Paper sx={{ 
            p: 4, 
            borderRadius: 2,
            boxShadow: 3,
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.05), rgba(255,255,255,0.02))'
          }}>
            {/* Metal Selection Tabs */}
            <Box sx={{ mb: 3 }}>
              <Tabs
                value={selectedMetalTab}
                onChange={handleMetalTabChange}
                centered
                sx={{
                  '& .MuiTab-root': {
                    minWidth: 120,
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                  },
                  '& .Mui-selected': {
                    color: '#4CAF50',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#4CAF50',
                    height: 3,
                  }
                }}
              >
                <Tab label="Platinum" />
                <Tab label="Palladium" />
                <Tab label="Rhodium" />
              </Tabs>
            </Box>
            
            {/* Timeframe Selection */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mb: 4,
              gap: 1
            }}>
              <Button 
                variant={timeFrame === 24 ? "contained" : "outlined"} 
                onClick={() => handleTimeFrameChange(24)}
                sx={{ 
                  minWidth: 120,
                  fontWeight: 600,
                  ...(timeFrame === 24 && {
                    bgcolor: '#4CAF50',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#45a049',
                    }
                  })
                }}
              >
                24 Hours
              </Button>
              <Button 
                variant={timeFrame === 168 ? "contained" : "outlined"} 
                onClick={() => handleTimeFrameChange(168)}
                sx={{ 
                  minWidth: 120,
                  fontWeight: 600,
                  ...(timeFrame === 168 && {
                    bgcolor: '#4CAF50',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#45a049',
                    }
                  })
                }}
              >
                7 Days
              </Button>
              <Button 
                variant={timeFrame === 720 ? "contained" : "outlined"} 
                onClick={() => handleTimeFrameChange(720)}
                sx={{ 
                  minWidth: 120,
                  fontWeight: 600,
                  ...(timeFrame === 720 && {
                    bgcolor: '#4CAF50',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#45a049',
                    }
                  })
                }}
              >
                30 Days
              </Button>
            </Box>
            
            {/* Chart */}
            {metalPricesLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 450 }}>
                <CircularProgress size={60} />
              </Box>
            ) : (
              <Box sx={{ 
                height: '450px',
                mb: 3,
                p: 2,
                bgcolor: 'background.paper',
                borderRadius: 1
              }}>
                {prepareMetalPricesChartData() ? (
                  <Chart type="line" data={prepareMetalPricesChartData()} options={metalPricesChartOptions} />
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <Typography color="text.secondary">No data available</Typography>
                  </Box>
                )}
              </Box>
            )}
            
            {/* Description */}
            <Box sx={{ 
              mt: 3, 
              p: 2, 
              bgcolor: 'info.light', 
              borderRadius: 1,
              borderLeft: '4px solid #4CAF50'
            }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                Track metal price trends to optimize your inventory purchases and pricing strategies.
                Higher prices in precious metals typically correspond with increased catalytic converter values.
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}
      
      <div
        className="tooltip"
        style={{ position: "fixed", pointerEvents: "none", zIndex: 9999 }}
      >
        {tooltipContent}
      </div>
    </Box>
  );
};

export default Dashboard;
